import { Injectable } from '@angular/core';
import { map, skipWhile, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';

import { UserKeys } from '@config/keys/user.keys';
import { MetafieldKeys } from '@config/keys/metafield.keys';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import {
  UserAgentPayload,
  ZerosixApiService,
} from '@core/services/api/zerosix/zerosix-api.service';

import { ICoupon } from '@core/models/cart.model';
import { DoodUserModel } from '@store/authentication/authentication.model';
import { IZerosixAsset, IZerosixCard, ZerosixAssetType } from '@core/models/zerosix.model';
import { UserService } from '../user/user.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { DistributionModeValues } from '@config/values/order.values';
import { ZeroSixPluginType } from '@core/models/plugin.model';

@Injectable({
  providedIn: 'root',
})
export class ZerosixService {
  constructor(
    private authSelector: AuthStoreSelector,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly zerosixApiService: ZerosixApiService,
    private readonly userService: UserService,
    private readonly settingsStoreSelector: SettingsStoreSelector,
    private readonly shopStoreSelector: ShopStoreSelector,
  ) {}

  private readonly orderingContext$: Observable<UserAgentPayload> = combineLatest({
    kiosk: this.settingsStoreSelector.selectKiosk,
    parameters: this.settingsStoreSelector.selectParameters$,
  }).pipe(
    map(({ kiosk, parameters }) => {
      if (kiosk.isKioskLayout) {
        return {
          ordering_context: 'kiosk',
        };
      }
      if (parameters?.distribution_mode === DistributionModeValues.ClickAndCollect) {
        return {
          ordering_context: 'click_and_collect',
        };
      }
      return {
        ordering_context: null,
      };
    }),
  );

  createZerosixUser$(phoneNumber: string): Observable<DoodUserModel> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, orderingContext]) => {
        return this.zerosixApiService.createZerosixUser$({
          marketplaceId,
          phoneNumber,
          ...orderingContext,
        });
      }),
    );
  }

  isUserZeroSixCardEnabledOnMarketplaceLevel$(): Observable<boolean> {
    return combineLatest([
      this.authSelector.selectUser$,
      this.marketplaceSelector.selectMarketplaceId$,
    ]).pipe(
      take(1),
      map(([user, marketplaceId]) => {
        if (!user || !user[UserKeys.Metafields] || !Array.isArray(user[UserKeys.Metafields])) {
          return false;
        }

        return !!user[UserKeys.Metafields]!.find(
          metafield =>
            metafield[MetafieldKeys.Handle] === `zerosix_card_id_marketplace_${marketplaceId}`,
        );
      }),
    );
  }

  isZerosixOtpIdentificationRequired$(): Observable<boolean> {
    return this.marketplaceSelector.selectMarketplacePlugins$.pipe(
      map(plugins => {
        return (
          (plugins?.find(plugin => plugin.type === 'zerosix') as ZeroSixPluginType)
            ?.otp_is_required ?? false
        );
      }),
      take(1),
    );
  }

  sendOtp$(phoneNumber: string): Observable<any> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, { ordering_context }]) =>
        this.zerosixApiService.sendOtp$({
          marketplaceId,
          phoneNumber,
          ordering_context,
        }),
      ),
    );
  }

  attachUserWithOtp$(code: string) {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, { ordering_context }]) =>
        this.zerosixApiService.attachUserByOtp$({
          marketplaceId,
          identification_code: code,
          ordering_context,
        }),
      ),
      switchMap(() => {
        return this.userService.loadUser$();
      }),
    );
  }

  getCard$(): Observable<IZerosixCard | undefined> {
    return combineLatest([
      this.marketplaceSelector.selectMarketplaceId$,
      this.orderingContext$,
      this.shopStoreSelector.selectActive$.pipe(skipWhile(shop => !shop?.id)), // on some a page, the active shop may not be initialized yet
    ]).pipe(
      take(1),
      switchMap(([marketplaceId, { ordering_context }, shop]) => {
        return this.zerosixApiService.getCard$({
          marketplaceId,
          shopId: shop?.id,
          ordering_context,
        });
      }),
    );
  }

  calculateCouponFromAsset(asset: IZerosixAsset): ICoupon | null {
    if (asset.type === ZerosixAssetType.VOUCHER) {
      return { code: asset.code };
    } else if (asset.type === ZerosixAssetType.REWARD) {
      return { key: 'reward_' + asset.id };
    }
    console.error('[Zerosix] Unknown asset type', asset.type);
    return null;
  }

  /**
   * It will update the anonymous user in firebase with the Zerosix metadata.
   * And update the user in the store.
   */
  attachUserByPhone$({ phoneNumber }: { phoneNumber: string }): Observable<DoodUserModel> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, { ordering_context }]) =>
        this.zerosixApiService.attachUserByPhone$({
          marketplaceId,
          phoneNumber,
          ordering_context,
        }),
      ),
      tap(response => {
        this.userService.setUser(response);
      }),
    );
  }

  /**
   * It will update the anonymous user in firebase with the Zerosix metadata.
   * And update the user in the store.
   */
  attachUserByOtp$({ code }: { code: string }): Observable<DoodUserModel> {
    return this.marketplaceSelector.selectMarketplaceId$.pipe(
      take(1),
      withLatestFrom(this.orderingContext$),
      switchMap(([marketplaceId, { ordering_context }]) =>
        this.zerosixApiService.attachUserByOtp$({
          marketplaceId,
          identification_code: code,
          ordering_context,
        }),
      ),
      tap(response => {
        this.userService.setUser(response);
      }),
    );
  }
}
